import { ApiFailure, type ErrorCode, type FailureReason } from '@signalvote/shared';

/** How many silent retries a transient failure gets before it surfaces. */
export const TRANSIENT_RETRY_LIMIT = 2;

/** Exponential backoff (ms) between silent retries, capped so it stays snappy. */
export function transientRetryDelay(attempt: number): number {
  return Math.min(300 * 2 ** attempt, 3000);
}

/** Per declared code, the payload a caller reads off that refusal. */
export type Payloads = { readonly [C in ErrorCode]?: unknown };

/** Per declared code, how a refusal's body becomes its payload. */
export type RefusalParsers<Pl extends Payloads> = { readonly [C in keyof Pl]: (body: Record<string, unknown>) => Pl[C] };

type Refused = Extract<FailureReason, { kind: 'refused' }>;

type DeclaredRefusal<Pl extends Payloads> = {
  [C in keyof Pl & ErrorCode]: Omit<Refused, 'code' | 'payload'> & { code: C; payload: Pl[C] };
}[keyof Pl & ErrorCode];

type UndeclaredRefusal<Pl extends Payloads> = Omit<Refused, 'code' | 'payload'> & {
  code: Exclude<ErrorCode, keyof Pl> | null;
  payload: null;
};

/** `FailureReason` with the `refused` arm split by the codes a write declared: each declared code carries its payload, any other a null one. */
export type WriteReason<Pl extends Payloads> = Exclude<FailureReason, { kind: 'refused' }> | DeclaredRefusal<Pl> | UndeclaredRefusal<Pl>;

/** The failure a write reports: an `ApiFailure` whose refusals are typed by the write's declaration. */
export interface WriteFailure<Pl extends Payloads = {}> extends ApiFailure {
  readonly reason: WriteReason<Pl>;
}

interface StaleTag {
  entity: string;
  id: string;
  currentVersion: number | null;
}

const staleTags = new WeakMap<object, StaleTag>();

/** Name the row a stale error is about, which the error the endpoint threw cannot. */
export function tagStale(error: object, tag: StaleTag): void {
  staleTags.set(error, tag);
}

function legacyFailure(error: object): ApiFailure | null {
  const { status, code, message, retryAfterSeconds } = error as { status?: unknown; code?: unknown; message?: unknown; retryAfterSeconds?: unknown };
  if (typeof status !== 'number') return null;
  return new ApiFailure(
    status,
    typeof code === 'string' ? code : null,
    typeof message === 'string' ? message : `HTTP ${status}`,
    typeof retryAfterSeconds === 'number' ? retryAfterSeconds : null,
    { ...error } as Record<string, unknown>,
  );
}

/**
 * The failure an error means: an `ApiFailure` as it is, or one read off a hook-local error class
 * that still carries `status`, `code` and `currentVersion` as fields. Null for anything else.
 */
export function asFailure(error: unknown): ApiFailure | null {
  if (typeof error !== 'object' || error === null) return null;
  const base = error instanceof ApiFailure ? error : legacyFailure(error);
  const tag = staleTags.get(error);
  if (!base || !tag) return base;
  return new ApiFailure(base.status, base.code, base.message, base.retryAfterSeconds, base.body, { kind: 'stale', ...tag });
}

/** A failure with the payload its write declared for the code, when it declared one. */
export function withPayload<Pl extends Payloads>(failure: ApiFailure, refusals: RefusalParsers<Pl> | undefined): WriteFailure<Pl> {
  const { reason } = failure;
  if (!refusals || reason.kind !== 'refused' || reason.code === null || reason.payload !== null) return failure as WriteFailure<Pl>;
  const parse = (refusals as Partial<Record<ErrorCode, (body: Record<string, unknown>) => unknown>>)[reason.code];
  if (!parse) return failure as WriteFailure<Pl>;
  return new ApiFailure(failure.status, failure.code, failure.message, failure.retryAfterSeconds, failure.body, {
    ...reason,
    payload: parse(failure.body),
  }) as WriteFailure<Pl>;
}

/** The payloads a write hook declared, read off the failure it reports. */
export type PayloadsOf<W> = W extends (...args: never[]) => { failure: WriteFailure<infer Pl> | null } ? Pl : {};

/**
 * The failure a write's `mutateAsync` rejected with, typed by that write's refusals. Anything
 * that is not an API failure is a bug, not an outcome, so it is thrown on.
 */
export function failureOf<W = () => { failure: WriteFailure | null }>(error: unknown): WriteFailure<PayloadsOf<W>> {
  const failure = asFailure(error);
  if (!failure) throw error;
  return failure as WriteFailure<PayloadsOf<W>>;
}

/** A failure a quiet retry can fix: no response arrived. A server answer, a refusal included, is final. */
export function isTransient(error: unknown): boolean {
  return asFailure(error)?.reason.kind === 'unreachable';
}
